import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  useLoaderData,
  useSearchParams,
  type LoaderFunctionArgs,
  type ShouldRevalidateFunctionArgs,
} from 'react-router-dom';
import { NativeSelect, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import {
  EmailLogKindSchema,
  EmailLogStatusSchema,
  type EmailLogListItem,
  type ListEmailLogQuery,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { ALL_WORKSPACES_QUERY } from '@/lib/test-workspaces/include-test-workspaces';
import { useTranslations } from '@/i18n/use-translations';
import { ListLeading } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { MailIcon, TagIcon } from '@/components/layout/icons';
import { FilterBar, FilterPill } from '@/components/filter-bar';
import { ListEmptyState } from '@/components/test-workspaces';
import { EmailStatusCell } from '@/components/email-status-cell';
import { ARIA_SORT, SortableHeader, useUrlTableSort, type SortValue } from '@/components/sortable-table';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_SEARCH_LENGTH = 200;
const SERVER_KEYS = ['q', 'workspaceId', 'kind', 'status'] as const;

const FIELDS = ['sent-at', 'kind', 'recipient', 'workspace', 'subject', 'status'] as const;
type Field = (typeof FIELDS)[number];

const COLUMN_WIDTHS: Partial<Record<Field, string>> = { 'sent-at': 'w-[150px]', status: 'w-[220px]' };

type WorkspaceOption = { readonly id: string; readonly name: string };

type LoaderData = {
  readonly rows: ReadonlyArray<EmailLogListItem>;
  readonly nextCursor: string | null;
  // Null when the workspace list could not be read.
  readonly workspaces: ReadonlyArray<WorkspaceOption> | null;
};

type EmailLogFilters = Omit<ListEmailLogQuery, 'cursor' | 'limit'>;

// Invalid address values are dropped rather than sent, so a hand-edited link never
// reaches the API's 400. The loader and „Mehr laden" read the address through here.
function queryFromParams(searchParams: URLSearchParams): EmailLogFilters {
  const q = searchParams.get('q')?.trim() ?? '';
  const workspaceId = searchParams.get('workspaceId') ?? '';
  const kind = EmailLogKindSchema.safeParse(searchParams.get('kind'));
  const status = EmailLogStatusSchema.safeParse(searchParams.get('status'));

  return {
    ...(q !== '' && q.length <= MAX_SEARCH_LENGTH ? { q } : {}),
    ...(UUID_RE.test(workspaceId) ? { workspaceId } : {}),
    ...(kind.success ? { kind: kind.data } : {}),
    ...(status.success ? { status: status.data } : {}),
  };
}

export async function emailLogLoader({ request }: LoaderFunctionArgs): Promise<LoaderData> {
  const query = queryFromParams(new URL(request.url).searchParams);

  const [log, workspaces] = await Promise.all([
    apiClient.adminEmailLog.list({ query }),
    apiClient.adminWorkspaces.list({ query: ALL_WORKSPACES_QUERY }),
  ]);

  if (!log.ok) {
    throw new Response('Failed to load e-mail log', { status: log.status });
  }

  return {
    rows: log.body.data,
    nextCursor: log.body.nextCursor,
    workspaces: workspaces.ok
      ? workspaces.body.data.map(({ id, name }) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'de'))
      : null,
  };
}

// Only the four server keys refetch; a sort change must neither fetch nor drop the
// rows already loaded with „Mehr laden".
export function shouldRevalidateEmailLog({ currentUrl, nextUrl }: ShouldRevalidateFunctionArgs): boolean {
  if (currentUrl.pathname !== nextUrl.pathname) return true;

  return SERVER_KEYS.some((key) => currentUrl.searchParams.get(key) !== nextUrl.searchParams.get(key));
}

const STATUSES = EmailLogStatusSchema.options;

export function EmailLogPage() {
  const t = useTranslations('pages.admin.email-log');
  const intl = useIntl();
  const { rows: firstPage, nextCursor, workspaces } = useLoaderData() as LoaderData;
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<ReadonlyArray<EmailLogListItem>>(firstPage);
  const [cursor, setCursor] = useState<string | null>(nextCursor);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setRows(firstPage);
    setCursor(nextCursor);
  }, [firstPage, nextCursor]);

  const placeholder = t('placeholder');
  const workspaceText = (row: EmailLogListItem) => row.workspaceName ?? row.workspaceId ?? placeholder;
  const sortKey = useCallback(
    (row: EmailLogListItem, field: Field): SortValue => {
      switch (field) {
        case 'sent-at':
          return Date.parse(row.sentAt);
        case 'kind':
          return t(`kinds.${row.kind}`);
        case 'recipient':
          return row.recipient;
        case 'workspace':
          return row.workspaceName ?? row.workspaceId;
        case 'subject':
          return row.subject;
        case 'status':
          return t(`statuses.${row.status}`);
      }
    },
    [t],
  );

  const { sorted, sort, toggleSort } = useUrlTableSort(rows, sortKey, { fields: FIELDS });

  const filters = queryFromParams(searchParams);
  const hasFilter = Object.keys(filters).length > 0;
  const selectedStatus = searchParams.get('status');

  function commitParam(key: string, value: string | null, options?: { replace?: boolean }): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        if (value === null || value === '') next.delete(key);
        else next.set(key, value);

        return next;
      },
      { replace: options?.replace ?? false },
    );
  }

  async function loadMore(): Promise<void> {
    if (cursor === null || loadingMore) return;
    setLoadingMore(true);

    try {
      const result = await apiClient.adminEmailLog.list({ query: { ...queryFromParams(searchParams), cursor } });

      if (result.ok) {
        setRows((prev) => [...prev, ...result.body.data]);
        setCursor(result.body.nextCursor);
      }
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <PageTopbar leading={<ListLeading title={t('title')} count={rows.length} icon={MailIcon} />} />
      <FilterBar
        fixedPills={STATUSES.map((status) => (
          <FilterPill
            key={status}
            icon={TagIcon}
            label={t(`statuses.${status}`)}
            active={selectedStatus === status}
            onToggle={() => commitParam('status', selectedStatus === status ? null : status)}
          />
        ))}
        searchValue={searchParams.get('q') ?? ''}
        searchPlaceholder={t('search-placeholder')}
        onSearchChange={(next, commit) => commitParam('q', next, { replace: commit.continuing })}
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-border bg-surface px-4 py-2">
        <div className="flex flex-col gap-1 text-sm text-text-tertiary">
          <label className="flex flex-col gap-1">
            {t('filters.workspace')}
            <NativeSelect
              size="sm"
              value={searchParams.get('workspaceId') ?? ''}
              aria-label={t('filters.workspace')}
              options={[
                { value: '', label: t('filters.all') },
                ...(workspaces ?? []).map(({ id, name }) => ({ value: id, label: name })),
              ]}
              onChange={(next) => commitParam('workspaceId', next)}
            />
          </label>
          {workspaces === null && <p className="text-sm text-text-tertiary">{t('filters.workspaces-unavailable')}</p>}
        </div>
        <label className="flex flex-col gap-1 text-sm text-text-tertiary">
          {t('filters.kind')}
          <NativeSelect
            size="sm"
            value={searchParams.get('kind') ?? ''}
            aria-label={t('filters.kind')}
            options={[
              { value: '', label: t('filters.all') },
              ...EmailLogKindSchema.options.map((kind) => ({ value: kind, label: t(`kinds.${kind}`) })),
            ]}
            onChange={(next) => commitParam('kind', next)}
          />
        </label>
      </div>
      {rows.length === 0 ? (
        <ListEmptyState serverEmpty={false} title={hasFilter ? t('empty-filtered') : t('empty')} />
      ) : (
        <>
          <Table flush>
            <TableHeader>
              <TableRow>
                {FIELDS.map((field) => (
                  <TableHead
                    key={field}
                    {...(COLUMN_WIDTHS[field] === undefined ? {} : { className: COLUMN_WIDTHS[field] })}
                    {...(sort !== null && sort.field === field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
                  >
                    <SortableHeader
                      field={field}
                      label={t(`columns.${field}`)}
                      ariaLabel={t('sort-label', { column: t(`columns.${field}`) })}
                      sort={sort}
                      onSort={toggleSort}
                    />
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="text-text-tertiary tabular-nums">
                    {intl.formatDate(row.sentAt, { dateStyle: 'short', timeStyle: 'short' })}
                  </TableCell>
                  <TableCell>{t(`kinds.${row.kind}`)}</TableCell>
                  <TableCell className="min-w-0 max-w-[280px]">
                    <div className="truncate" title={row.recipient}>
                      {row.recipient}
                    </div>
                  </TableCell>
                  <TableCell>{workspaceText(row)}</TableCell>
                  <TableCell className="min-w-0 max-w-[320px]">
                    <div className="truncate" title={row.subject}>
                      {row.subject}
                    </div>
                  </TableCell>
                  <TableCell>
                    <EmailStatusCell status={row.status} statusAt={row.statusAt} failureReason={row.failureReason} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {cursor !== null && (
            <div className="flex justify-center p-4">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex h-8 items-center gap-1 rounded-sm border border-border bg-surface px-3 text-base-sm font-medium text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-60"
              >
                {loadingMore ? t('load-more-loading') : t('load-more')}
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
